import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { reportsAPI, projectsAPI, filesAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import {
  Search,
  FileText,
  CheckCircle2,
  Clock,
  AlertTriangle,
  Eye,
  Send,
  RotateCcw,
  ThumbsUp,
  ArrowRight,
  Download,
  File,
  Users
} from 'lucide-react';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';

const STATUS_LABELS = {
  draft: { label: 'Draft', color: 'bg-gray-100 text-gray-700', icon: FileText },
  pending_admin_review: { label: 'Pending Review', color: 'bg-amber-100 text-amber-700', icon: Clock },
  revision_requested: { label: 'Revision Requested', color: 'bg-orange-100 text-orange-700', icon: RotateCcw },
  approved: { label: 'Approved', color: 'bg-emerald-100 text-emerald-700', icon: CheckCircle2 },
  released: { label: 'Released to Donor', color: 'bg-blue-100 text-blue-700', icon: Send },
  donor_approved: { label: 'Accepted by Donor', color: 'bg-emerald-100 text-emerald-700', icon: ThumbsUp },
  donor_revision_requested: { label: 'Donor Requested Revision', color: 'bg-amber-100 text-amber-700', icon: RotateCcw },
  verified: { label: 'Verified', color: 'bg-green-100 text-green-700', icon: CheckCircle2 },
  flagged: { label: 'Flagged', color: 'bg-red-100 text-red-700', icon: AlertTriangle }
};

export default function ReportsPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isDonor = user?.role === 'donor';
  const [reports, setReports] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [projectFilter, setProjectFilter] = useState('all');
  const [showReviewDialog, setShowReviewDialog] = useState(false);
  const [selectedReport, setSelectedReport] = useState(null);
  const [feedback, setFeedback] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [reportsRes, projectsRes] = await Promise.all([
        reportsAPI.list(),
        projectsAPI.list()
      ]);
      setReports(reportsRes.data || []);
      setProjects(projectsRes.data || []);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load reports');
    } finally {
      setLoading(false);
    }
  };

  const handleReviewAction = async (action) => {
    if (!selectedReport) return;
    
    try {
      await reportsAPI.review(selectedReport.report_id, action, feedback);
      toast.success(`Report ${action === 'approve' ? 'approved' : action === 'release' ? 'released to donor' : 'sent back for revision'}`);
      setShowReviewDialog(false);
      setSelectedReport(null);
      setFeedback('');
      fetchData();
    } catch (error) {
      console.error('Error reviewing report:', error);
      toast.error('Failed to update report');
    }
  };

  const getStatusBadge = (status) => {
    const statusInfo = STATUS_LABELS[status] || { label: status, color: 'bg-gray-100 text-gray-700', icon: FileText };
    const IconComponent = statusInfo.icon;
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${statusInfo.color}`}>
        <IconComponent className="w-3 h-3" />
        {statusInfo.label}
      </span>
    );
  };

  const filteredReports = reports.filter(report => {
    const matchesSearch = (report.title || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
                          (report.agent_name || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
                          (report.project_title || '').toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = statusFilter === 'all' || report.status === statusFilter;
    const matchesProject = projectFilter === 'all' || report.project_id === projectFilter;
    return matchesSearch && matchesStatus && matchesProject;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 page-enter" data-testid="reports-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl lg:text-3xl font-bold text-slate-900 font-['Manrope']">Field Reports</h1>
        <p className="text-slate-500 mt-1">{reports.length} reports total</p>
      </div>

      {/* Filters */}
      <Card className="stat-card">
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Search reports..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
                data-testid="search-reports-input"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[200px]" data-testid="status-filter-select">
                <SelectValue placeholder="Filter by status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="draft">Draft</SelectItem>
                <SelectItem value="pending_admin_review">Pending Review</SelectItem>
                <SelectItem value="revision_requested">Revision Requested</SelectItem>
                <SelectItem value="approved">Approved</SelectItem>
                <SelectItem value="released">Released</SelectItem>
                <SelectItem value="donor_approved">Accepted by Donor</SelectItem>
                <SelectItem value="donor_revision_requested">Donor Revision Requested</SelectItem>
              </SelectContent>
            </Select>
            <Select value={projectFilter} onValueChange={setProjectFilter}>
              <SelectTrigger className="w-[200px]" data-testid="project-filter-select">
                <SelectValue placeholder="Filter by project" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Projects</SelectItem>
                {projects.map((project) => (
                  <SelectItem key={project.project_id} value={project.project_id}>
                    {project.title}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Reports List */}
      {filteredReports.length > 0 ? (
        <div className="space-y-4">
          {filteredReports.map((report) => (
            <Card key={report.report_id} className="stat-card" data-testid={`report-${report.report_id}`}>
              <CardContent className="p-6">
                <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      {getStatusBadge(report.status)}
                      {report.agent_slot && (
                        <Badge variant="outline" className="text-xs gap-1">
                          <Users className="w-3 h-3" />Agent {report.agent_slot}
                        </Badge>
                      )}
                      <h3 className="font-semibold text-lg text-slate-900 font-['Manrope']">
                        {report.title}
                      </h3>
                    </div>
                    
                    <p className="text-sm text-slate-600 mb-3 line-clamp-2">{report.content}</p>
                    
                    <div className="flex flex-wrap gap-4 text-sm text-slate-500">
                      <span>by <strong>{report.agent_name}</strong></span>
                      <span>Project: <strong>{report.project_title}</strong></span>
                      <span>{new Date(report.created_at).toLocaleDateString()}</span>
                      {report.revision_count > 0 && (
                        <span className="text-amber-600">Revision #{report.revision_count}</span>
                      )}
                    </div>

                    {report.admin_feedback && (
                      <div className="mt-3 p-3 bg-amber-50 rounded-lg border border-amber-200">
                        <p className="text-sm text-amber-800">
                          <strong>Admin Feedback:</strong> {report.admin_feedback}
                        </p>
                      </div>
                    )}

                    {report.donor_feedback && (
                      <div className={`mt-3 p-3 rounded-lg border ${report.status === 'donor_approved' ? 'bg-emerald-50 border-emerald-200' : 'bg-blue-50 border-blue-200'}`}>
                        <p className={`text-sm ${report.status === 'donor_approved' ? 'text-emerald-800' : 'text-blue-800'}`}>
                          <strong>Donor Feedback:</strong> {report.donor_feedback}
                        </p>
                      </div>
                    )}

                    {report.findings && (
                      <div className="mt-3">
                        <p className="text-sm text-slate-500"><strong>Findings:</strong> {report.findings}</p>
                      </div>
                    )}
                  </div>
                  
                  {/* Action Buttons */}
                  <div className="flex gap-2 flex-shrink-0">
                    {isDonor && (
                      <Button
                        size="sm"
                        onClick={() => navigate(`/donor/reports/${report.report_id}`)}
                        data-testid={`view-report-${report.report_id}`}
                      >
                        <Eye className="w-4 h-4 mr-1" />
                        View Report
                      </Button>
                    )}

                    {!isDonor && report.status === 'pending_admin_review' && (
                      <Button
                        size="sm"
                        onClick={() => {
                          setSelectedReport(report);
                          setShowReviewDialog(true);
                        }}
                        data-testid={`review-report-${report.report_id}`}
                      >
                        <Eye className="w-4 h-4 mr-1" />
                        Review
                      </Button>
                    )}

                    {report.status === 'approved' && (
                      <Button
                        size="sm"
                        className="bg-green-600 hover:bg-green-700"
                        onClick={async () => {
                          try {
                            await reportsAPI.review(report.report_id, 'release', '');
                            toast.success('Report released to donor');
                            fetchData();
                          } catch (error) {
                            toast.error('Failed to release report');
                          }
                        }}
                        data-testid={`release-report-${report.report_id}`}
                      >
                        <Send className="w-4 h-4 mr-1" />
                        Release to Donor
                      </Button>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <Card className="stat-card">
          <CardContent className="p-12 text-center">
            <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <FileText className="w-8 h-8 text-slate-400" />
            </div>
            <h3 className="text-lg font-semibold text-slate-900 mb-2">No reports found</h3>
            <p className="text-slate-500">
              {searchQuery || statusFilter !== 'all' || projectFilter !== 'all'
                ? 'Try adjusting your filters'
                : 'Field reports will appear here once submitted by agents'}
            </p>
          </CardContent>
        </Card>
      )}

      {/* Review Dialog */}
      <Dialog open={showReviewDialog} onOpenChange={(open) => {
        setShowReviewDialog(open);
        if (!open) {
          setSelectedReport(null);
          setFeedback('');
        }
      }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-['Manrope']">Review Report</DialogTitle>
            <DialogDescription>
              Review the report and take action
            </DialogDescription>
          </DialogHeader>
          
          {selectedReport && (
            <div className="space-y-4 mt-4">
              <div>
                <Label className="text-sm text-slate-500">Report Title</Label>
                <p className="font-medium">{selectedReport.title}</p>
              </div>
              
              <div>
                <Label className="text-sm text-slate-500">Project</Label>
                <p>{selectedReport.project_title}</p>
              </div>
              
              <div>
                <Label className="text-sm text-slate-500">Submitted by</Label>
                <p>{selectedReport.agent_name}</p>
              </div>
              
              <div>
                <Label className="text-sm text-slate-500">Content</Label>
                <p className="text-slate-700 whitespace-pre-wrap bg-slate-50 p-3 rounded-lg">
                  {selectedReport.content}
                </p>
              </div>

              {selectedReport.findings && (
                <div>
                  <Label className="text-sm text-slate-500">Findings</Label>
                  <p className="text-slate-700 bg-slate-50 p-3 rounded-lg">{selectedReport.findings}</p>
                </div>
              )}

              {selectedReport.recommendations && (
                <div>
                  <Label className="text-sm text-slate-500">Recommendations</Label>
                  <p className="text-slate-700 bg-slate-50 p-3 rounded-lg">{selectedReport.recommendations}</p>
                </div>
              )}

              {/* Indicator Comparison Table */}
              {selectedReport.indicator_verification?.length > 0 && (
                <div data-testid="admin-indicator-comparison">
                  <Label className="text-sm text-slate-500 mb-2 block">Indicator Verification</Label>
                  <div className="overflow-x-auto rounded-lg border border-slate-200">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="bg-slate-50 border-b border-slate-200">
                          <th className="text-left p-3 font-medium text-slate-700">Indicator</th>
                          <th className="text-left p-3 font-medium text-slate-700">Donor Target</th>
                          <th className="text-left p-3 font-medium text-slate-700">Verified Value</th>
                          <th className="text-left p-3 font-medium text-slate-700">Agent Notes</th>
                        </tr>
                      </thead>
                      <tbody>
                        {selectedReport.indicator_verification.map((iv, idx) => (
                          <tr key={idx} className="border-b border-slate-100 last:border-0">
                            <td className="p-3 font-medium text-slate-900">{iv.indicator_name}</td>
                            <td className="p-3 text-slate-600">{iv.donor_value || '-'}</td>
                            <td className="p-3">
                              {iv.verified_value ? (
                                <span className={`font-medium ${iv.verified_value === iv.donor_value ? 'text-emerald-600' : 'text-amber-600'}`}>
                                  {iv.verified_value}
                                </span>
                              ) : (
                                <span className="text-slate-400 italic">Not verified</span>
                              )}
                            </td>
                            <td className="p-3 text-slate-500 text-xs">{iv.agent_notes || '-'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Evidence Files */}
              {selectedReport.evidence_file_ids?.length > 0 && (
                <div>
                  <Label className="text-sm text-slate-500 mb-2 block">Evidence Files ({selectedReport.evidence_file_ids.length})</Label>
                  <div className="flex flex-wrap gap-2">
                    {selectedReport.evidence_file_ids.map((fid) => (
                      <a key={fid} href={filesAPI.view(fid)} target="_blank" rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-xs px-3 py-2 bg-slate-100 rounded-lg hover:bg-slate-200 transition-colors text-slate-700">
                        <File className="w-3 h-3" />{fid.substring(0, 15)}...<Download className="w-3 h-3 ml-1" />
                      </a>
                    ))}
                  </div>
                </div>
              )}
              
              <div>
                <Label htmlFor="feedback">Feedback (optional)</Label>
                <Textarea
                  id="feedback"
                  placeholder="Provide feedback for the agent..."
                  value={feedback}
                  onChange={(e) => setFeedback(e.target.value)}
                  rows={3}
                />
              </div>
              
              <div className="flex gap-3 pt-4 border-t">
                <Button
                  variant="outline"
                  className="flex-1 text-orange-600 hover:bg-orange-50"
                  onClick={() => handleReviewAction('request_revision')}
                >
                  <RotateCcw className="w-4 h-4 mr-1" />
                  Request Revision
                </Button>
                <Button
                  className="flex-1 bg-emerald-600 hover:bg-emerald-700"
                  onClick={() => handleReviewAction('approve')}
                >
                  <CheckCircle2 className="w-4 h-4 mr-1" />
                  Approve
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
